import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Autocomplete,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import { useSelector, useDispatch } from "react-redux";
import { AppDispatch, RootState } from "../../../../redux/store";
import { fetchClasses } from "../../../../redux/admin/adminOperations";

interface CreatePriceDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (priceData: {
    specialType?: string | null;
    numberOfClasses: number;
    amount: number;
    currency: string;
    classId?: number | null;
    isAdminOnly?: boolean;
  }) => void;
}

const CreatePriceDialog: React.FC<CreatePriceDialogProps> = ({
  open,
  onClose,
  onSave,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch<AppDispatch>();
  const { classes } = useSelector((state: RootState) => state.admin);

  const [formData, setFormData] = useState({
    priceType: "general", // "general" or "special"
    specialType: "",
    numberOfClasses: "",
    amount: "",
    currency: "chf",
    classId: "",
    isAdminOnly: false,
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const currencies = [{ value: "chf", label: "CHF (Fr)" }];

  useEffect(() => {
    if (open) {
      dispatch(
        fetchClasses({
          page: 1,
          limit: 100,
          sortBy: "id",
          sortOrder: "asc",
          search: "",
        })
      );
    }
  }, [open, dispatch]);

  const handleInputChange = (field: string, value: string | boolean) => {
    // If enabling admin-only, set amount to 0 and clear any amount errors
    if (field === "isAdminOnly" && value === true) {
      setFormData((prev) => ({ ...prev, [field]: value, amount: "0" }));
      setErrors((prev) => ({ ...prev, amount: "" }));
    } else if (field === "isAdminOnly" && value === false) {
      // If disabling admin-only, clear the amount so user must enter a valid price
      setFormData((prev) => ({ ...prev, [field]: value, amount: "" }));
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }));
      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: "" }));
      }
    }
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    // Validate special type and class selection for special prices
    if (formData.priceType === "special") {
      if (!formData.specialType.trim()) {
        newErrors.specialType = intl.formatMessage({
          id: "admin.dashboard.specialTypeRequired",
        });
      }
      if (!formData.classId) {
        newErrors.classId = intl.formatMessage({
          id: "admin.dashboard.classRequired",
        });
      }
    }

    // Always validate numberOfClasses (required for all price types)
    if (!formData.numberOfClasses.trim()) {
      newErrors.numberOfClasses = intl.formatMessage({
        id: "admin.dashboard.numberOfClassesRequired",
      });
    } else {
      const numClasses = parseInt(formData.numberOfClasses);
      if (isNaN(numClasses) || numClasses <= 0) {
        newErrors.numberOfClasses = intl.formatMessage({
          id: "admin.dashboard.numberOfClassesInvalid",
        });
      }
    }

    if (!formData.amount.trim()) {
      newErrors.amount = intl.formatMessage({
        id: "admin.dashboard.amountRequired",
      });
    } else {
      const amount = parseFloat(formData.amount);
      // Allow 0 for admin-only prices
      if (isNaN(amount) || (!formData.isAdminOnly && amount <= 0)) {
        newErrors.amount = intl.formatMessage({
          id: "admin.dashboard.amountInvalid",
        });
      } else if (amount < 0) {
        newErrors.amount = intl.formatMessage({
          id: "admin.dashboard.amountNegative",
        });
      }
    }

    if (!formData.currency.trim()) {
      newErrors.currency = intl.formatMessage({
        id: "admin.dashboard.currencyRequired",
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (validateForm()) {
      const amount = Math.round(parseFloat(formData.amount) * 100); // Convert to cents
      const priceData: {
        specialType?: string | null;
        numberOfClasses: number;
        amount: number;
        currency: string;
        classId?: number | null;
        isAdminOnly?: boolean;
      } = {
        amount,
        currency: formData.currency,
        numberOfClasses: parseInt(formData.numberOfClasses),
        isAdminOnly: formData.isAdminOnly,
      };

      if (formData.priceType === "special") {
        priceData.specialType = formData.specialType;
        priceData.classId = formData.classId
          ? parseInt(formData.classId)
          : null;
      } else {
        priceData.specialType = null;
        priceData.classId = null;
      }

      onSave(priceData);
      handleClose();
    }
  };

  const handleClose = () => {
    setFormData({
      priceType: "general",
      specialType: "",
      numberOfClasses: "",
      amount: "",
      currency: "chf",
      classId: "",
      isAdminOnly: false,
    });
    setErrors({});
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <FormattedMessage id="admin.dashboard.createPrice" />
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <FormControl fullWidth>
            <InputLabel>
              <FormattedMessage id="admin.dashboard.priceType" />
            </InputLabel>
            <Select
              value={formData.priceType}
              label={intl.formatMessage({ id: "admin.dashboard.priceType" })}
              onChange={(e) => handleInputChange("priceType", e.target.value)}
            >
              <MenuItem value="general">
                <FormattedMessage id="admin.dashboard.generalPrice" />
              </MenuItem>
              <MenuItem value="special">
                <FormattedMessage id="admin.dashboard.specialPrice" />
              </MenuItem>
            </Select>
          </FormControl>

          {formData.priceType === "special" && (
            <>
              <TextField
                fullWidth
                label={intl.formatMessage({
                  id: "admin.dashboard.specialType",
                })}
                value={formData.specialType}
                onChange={(e) =>
                  handleInputChange("specialType", e.target.value)
                }
                error={!!errors.specialType}
                helperText={errors.specialType}
                placeholder={intl.formatMessage({
                  id: "admin.dashboard.specialTypePlaceholder",
                })}
              />

              <FormControl fullWidth error={!!errors.classId}>
                <Autocomplete
                  options={classes.filter((cls) => cls.isSpecial)}
                  getOptionLabel={(option) =>
                    option.title?.en ||
                    option.title?.fr ||
                    option.title?.uk ||
                    `Class ${option.id}`
                  }
                  value={
                    classes.find(
                      (cls) => cls.id.toString() === formData.classId
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    handleInputChange(
                      "classId",
                      newValue ? newValue.id.toString() : ""
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={intl.formatMessage({
                        id: "admin.dashboard.selectSpecialClass",
                      })}
                      error={!!errors.classId}
                      helperText={errors.classId}
                    />
                  )}
                />
              </FormControl>
            </>
          )}

          {/* Number of classes is now required for all price types */}
          <TextField
            fullWidth
            label={intl.formatMessage({
              id: "admin.dashboard.numberOfClasses",
            })}
            value={formData.numberOfClasses}
            onChange={(e) =>
              handleInputChange("numberOfClasses", e.target.value)
            }
            error={!!errors.numberOfClasses}
            helperText={errors.numberOfClasses}
            type="number"
            inputProps={{ min: "1", step: "1" }}
          />

          <TextField
            fullWidth
            label={intl.formatMessage({ id: "admin.dashboard.amount" })}
            value={formData.amount}
            onChange={(e) => handleInputChange("amount", e.target.value)}
            error={!!errors.amount}
            helperText={errors.amount}
            type="number"
            inputProps={{ step: "0.01", min: "0" }}
            disabled={formData.isAdminOnly}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">Fr</InputAdornment>
              ),
            }}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={formData.isAdminOnly}
                onChange={(e) =>
                  handleInputChange("isAdminOnly", e.target.checked)
                }
              />
            }
            label={intl.formatMessage({ id: "admin.dashboard.adminOnlyPrice" })}
          />

          <FormControl fullWidth error={!!errors.currency}>
            <InputLabel>
              <FormattedMessage id="admin.dashboard.currency" />
            </InputLabel>
            <Select
              value={formData.currency}
              label={intl.formatMessage({ id: "admin.dashboard.currency" })}
              onChange={(e) => handleInputChange("currency", e.target.value)}
            >
              {currencies.map((currency) => (
                <MenuItem key={currency.value} value={currency.value}>
                  {currency.label}
                </MenuItem>
              ))}
            </Select>
            {errors.currency && (
              <Box sx={{ color: "error.main", fontSize: "0.75rem", mt: 0.5 }}>
                {errors.currency}
              </Box>
            )}
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>
          <FormattedMessage id="admin.dashboard.cancel" />
        </Button>
        <Button onClick={handleSave} variant="contained">
          <FormattedMessage id="admin.dashboard.create" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreatePriceDialog;
